// Permisos de marcado de un cliente corporativo: el nivel es LA TRONCAL.
//
// Un corporativo no tiene un ANI ni uno por línea: tiene una troncal con un pool de ANIs (30
// DIDs por default). Los permisos se piden por troncal —"esta troncal puede llamar local,
// nacional y móvil, internacional no"— y eso impacta a cada uno de sus ANIs.
//
// ⚠️ DÓNDE VIVE (layout pedido por Carlos): esto NO es una pestaña. Se renderiza **dentro de
// la pestaña Troncal, en la fila de la troncal** (botón `🔒 Permisos` → `TroncalCliente.js`),
// porque la configuración de un servicio va con su entidad — igual que los permisos de una
// línea se editan desde su fila en la pestaña Líneas. Un cliente de troncal **no tiene pestaña
// Permisos**: `ClientesV2` la saca de `tabsVisibles` cuando el producto es troncal.
//
// El residencial sí tiene pestaña: va por `PermisosCliente` (plantilla del cliente + una
// sección por línea).
//
// Lo que se muestra es siempre lo que HACE el AGI: `asterisk.ani_permissions` es lo único que
// lee el camino de la llamada, y un DID sin fila marca por `call_categories.permitido_default`.
// La troncal no tiene una tabla propia a propósito: su juego de permisos es el de sus ANIs, y
// `uniforme` avisa cuando alguno dejó de coincidir.
//
// ⚠️ Estos DIDs NO aparecen en la pantalla global de permisos (`/v1/permissions/all`): esa
// arma la lista desde `netvoice.sip_credentials` y una troncal no tiene credenciales (IP-trust).
import { useState, useEffect, useCallback } from "react";
import { useConfirm } from "./ConfirmDialog";
import { permFetch, safe, mono, Matriz } from "./permisosComunes";

const chip = (fondo, color, borde) => ({
  fontSize: 10, padding: "1px 6px", borderRadius: 999,
  background: fondo, color, border: `1px solid ${borde}`,
});

/* ── Permisos de UN DID (excepción dentro de la troncal) ── */
function PermisosDid({ did, cats, iniciales, tienePropios, alineado, editable, onGuardado }) {
  const [valores, setValores] = useState(iniciales);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // Los valores llegan en la misma respuesta que el estado de la troncal: pedir
  // `/permissions/ani/{did}` acá serían 30 requests al desplegar la lista.
  useEffect(() => { setValores(iniciales); setMsg(null); setErr(null); }, [iniciales]);

  const guardar = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      // init crea las filas si el ANI nunca se tocó (heredando la plantilla del cliente);
      // bulk las actualiza con lo que se eligió acá.
      await safe(() => permFetch(`/permissions/ani/${encodeURIComponent(did)}/init`, "POST"));
      await permFetch(`/permissions/ani/${encodeURIComponent(did)}/bulk`, "PUT", valores);
      setMsg("Guardado");
      onGuardado?.();
    } catch (e) {
      setErr(e.message || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: "12px 16px", background: "var(--bg-base)" }}>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
        {alineado
          ? `Este número marca igual que la troncal.${tienePropios ? "" : " No tiene filas propias: usa el default de cada categoría."}`
          : "Este número está apartado de la troncal: marca distinto que el resto del pool."}
      </div>
      <Matriz cats={cats} valores={valores} editable={editable}
        onChange={(k, v) => setValores(p => ({ ...p, [k]: v }))}/>
      {err && <div style={{ marginTop: 10, fontSize: 12, color: "var(--danger)" }}>{err}</div>}
      {msg && <div style={{ marginTop: 10, fontSize: 12, color: "var(--success)", fontWeight: 600 }}>✓ {msg}</div>}
      {editable && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <button className="nv-btn nv-btn-primary" disabled={busy} onClick={guardar}>
            {busy ? "Guardando…" : `Guardar solo ${did}`}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── La matriz de la troncal: se escribe en todos sus ANIs ── */
function MatrizDeLaTroncal({ troncal, estado, editable, onGuardado, onError }) {
  const [valores, setValores] = useState(estado.permisos);
  const [busy, setBusy] = useState(false);
  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();
  const cats = estado.categories || [];
  const desalineados = estado.dids_desalineados || [];
  const difiere = estado.difiere_de_general || [];

  useEffect(() => { setValores(estado.permisos); }, [estado.permisos]);

  const sucio = cats.some(c => !!valores[c.nombre] !== !!estado.permisos[c.nombre]);

  const guardar = async () => {
    const permitidas = cats.filter(c => valores[c.nombre]).map(c => c.label);
    const nAnis = (estado.anis || []).length;
    let aviso = `Los permisos de la troncal «${troncal.nombre || troncal.id}» quedan así para ` +
      `sus ${nAnis} ANIs:\n\nPermitido: ${permitidas.join(", ") || "(nada)"}\n` +
      `Bloqueado: ${cats.filter(c => !valores[c.nombre]).map(c => c.label).join(", ") || "(nada)"}`;
    if (desalineados.length) {
      aviso += `\n\n⚠️ Se pierden los permisos propios de ${desalineados.length} ` +
        `número(s) apartado(s): ${desalineados.slice(0, 5).join(", ")}` +
        `${desalineados.length > 5 ? "…" : ""}`;
    }
    if (!(await confirmar(`${aviso}\n\n¿Seguimos?`))) return;
    setBusy(true);
    try {
      const r = await permFetch(`/troncales/${troncal.id}/permisos`, "PUT", { permisos: valores });
      onGuardado(r?.anis_actualizados ?? r?.dids_actualizados ?? nAnis);
    } catch (e) {
      onError(e.message || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {dialogoConfirm}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>Permisos de marcado de la troncal</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
          aplica a los {(estado.anis || []).length} ANIs del pool
        </div>
      </div>

      {!estado.uniforme && (
        <div style={{ fontSize: 11.5, color: "var(--warning,#d97706)", marginBottom: 10, lineHeight: 1.5 }}>
          ⚠️ {desalineados.length} de {estado.dids.length} números marcan distinto que la troncal
          ({desalineados.slice(0, 4).join(", ")}{desalineados.length > 4 ? "…" : ""}). Lo de acá
          abajo es lo que hace la <strong>cabecera</strong>; guardar alinea a todos.
        </div>
      )}
      {estado.general_definida && difiere.length > 0 && (
        <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 10 }}>
          Difiere de la general del cliente en: <strong>{difiere.join(", ")}</strong>.
        </div>
      )}

      <Matriz cats={cats} valores={valores} editable={editable && !busy}
        onChange={(k, v) => setValores(p => ({ ...p, [k]: v }))}/>

      {editable && (
        <div style={{ display: "flex", gap: 9, justifyContent: "flex-end", marginTop: 12 }}>
          {sucio && (
            <button className="nv-btn nv-btn-ghost" disabled={busy}
              onClick={() => setValores(estado.permisos)}>Descartar</button>
          )}
          <button className="nv-btn nv-btn-primary" disabled={busy || (!sucio && estado.uniforme)}
            onClick={guardar}
            title={!sucio && estado.uniforme ? "No hay cambios que aplicar" : undefined}>
            {busy ? "Guardando…" : (estado.uniforme ? "Guardar permisos de la troncal"
                                                    : "Guardar y alinear todos los números")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Permisos de UNA troncal: la sección que vive en su fila de la pestaña Troncal ──
   `editable`    = puede escribir los permisos DE LA TRONCAL (rol admin, igual que el
                   require_roles del PUT /v1/troncales/{id}/permisos).
   `editableDid` = puede escribir los de UN número suelto (sac/admin/superadmin, igual que el
                   PUT /v1/permissions/ani/{ani}/bulk). Se separan para no ampliar ni recortar
                   lo que cada rol ya podía hacer al mover la pantalla de lugar. */
export function PermisosDeUnaTroncal({ troncal, editable = false, editableDid = false }) {
  const [estado, setEstado] = useState(null);
  const [didAbierto, setDidAbierto] = useState(null);
  const [verDids, setVerDids] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const cargar = useCallback(async () => {
    const r = await safe(() => permFetch(`/permissions/troncal/${troncal.id}`));
    if (r) setEstado(r); else setErr("No se pudieron cargar los permisos de la troncal");
  }, [troncal.id]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!estado) {
    return <div style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-secondary)" }}>
      {err || "Cargando permisos de la troncal…"}</div>;
  }

  const dids = estado.dids || [];
  return (
    <div style={{ padding: "14px 16px" }}>
      {err && <div style={{ marginBottom: 10, fontSize: 12, color: "var(--danger)" }}>{err}</div>}
      {msg && <div style={{ marginBottom: 10, padding: "8px 12px", background: "var(--success-bg)",
        color: "var(--success)", borderRadius: "var(--r-sm)", fontSize: 12, fontWeight: 600 }}>{msg}</div>}

      <MatrizDeLaTroncal troncal={troncal} estado={estado} editable={editable}
        onGuardado={(n) => { setErr(null); setMsg(`Aplicado a los ${n} ANIs de la troncal.`);
                             setDidAbierto(null); cargar(); }}
        onError={(e) => { setMsg(null); setErr(e); }}/>

      <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
        <button className="nv-btn nv-btn-ghost" style={{ padding: "4px 10px", fontSize: 11.5 }}
          onClick={() => { setVerDids(!verDids); setDidAbierto(null); }}
          title="Permisos de un número suelto, cuando tiene que apartarse de la troncal">
          {verDids ? "▾" : "▸"} Por número ({dids.length})
          {estado.dids_desalineados?.length ? ` · ${estado.dids_desalineados.length} apartado(s)` : ""}
        </button>

        {verDids && dids.map(d => {
          const open = didAbierto === d.did_number;
          const apartado = d.alineado === false;
          return (
            <div key={d.did_number} style={{ borderTop: "1px solid var(--border)" }}>
              <button
                onClick={() => setDidAbierto(open ? null : d.did_number)}
                style={{ width: "100%", textAlign: "left", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 4px",
                  background: open ? "var(--bg-raised)" : "transparent",
                  border: "none", color: "var(--text-primary)" }}>
                <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{open ? "▾" : "▸"}</span>
                <span style={{ ...mono, fontWeight: 600, fontSize: 13 }}>{d.did_number}</span>
                {d.is_principal && (
                  <span style={chip("var(--brand-subtle)", "var(--brand)", "var(--border-active)")}>Cabecera</span>
                )}
                {d.status === "suspended" && (
                  <span style={chip("var(--danger-bg,rgba(239,68,68,.08))", "var(--danger)", "rgba(239,68,68,.25)")}>
                    suspendido
                  </span>
                )}
                {apartado && (
                  <span style={chip("var(--warning-bg,rgba(217,119,6,.08))", "var(--warning,#d97706)", "rgba(217,119,6,.25)")}>
                    distinto de la troncal
                  </span>
                )}
              </button>
              {open && (
                <PermisosDid did={d.did_number} cats={estado.categories}
                  iniciales={d.permisos} tienePropios={d.tiene_permisos_propios}
                  alineado={d.alineado !== false} editable={editableDid}
                  onGuardado={() => cargar()}/>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
